/**
 * Which e-signature placements this application actually wants signed.
 *
 * A signature can be gated by conditions over the advisor's answers, exactly as
 * a PDF entry or a signer role can (`SignatureField.conditions`). Suppressing
 * one has to happen in two places that cannot see each other:
 *
 * 1. `collectAnchorStamps` — the invisible white anchor text must not be written
 *    into the generated PDF.
 * 2. `BmoDataBuilder.BuildDocuments` (C#) — BMO must not be told to look for it.
 *
 * Describing a signature in (2) that (1) never stamped produces a package where
 * OneSpan cannot find a signature, which surfaces only when somebody tries to
 * sign. So this module is the single place the rule lives on the browser side,
 * and `suppressedSignatures` is what travels to the server as a narrowing claim
 * on `ESignSubmission` — the server has no condition evaluator (the values never
 * reach it), the same reason `generatedPdfEntryIds` exists.
 */
import { evaluateConditions } from './evaluateConditions';
import { stepsWithPdfTemplates } from './templateTypes';
import type { SignatureField, WorkflowStep, WorkflowTemplate } from './templateTypes';
import type { FieldValue } from './values';

/**
 * One signature the advisor's answers have gated out.
 *
 * Identified by the pair rather than the anchor alone: anchors are unique within
 * a document by construction, not across them, so two PDFs in the same package
 * can legitimately carry the same anchor string.
 */
export type SuppressedSignature = {
  /** `StepFileEntry.id` of the PDF this signature is placed in. */
  pdfEntryId: string;
  /** The signature's `textAnchor`, brackets included. */
  textAnchor: string;
};

/**
 * Whether this signature's gating conditions pass.
 *
 * Unconditional signatures — every one authored before the feature existed —
 * have no conditions and are always active, since `evaluateConditions` returns
 * true for an empty list.
 */
export function isSignatureActive(
  field: SignatureField,
  values: Record<string, FieldValue>,
): boolean {
  return evaluateConditions(field.conditions, field.conditionCombinator, values);
}

/**
 * Every signature suppressed by its own conditions, across every document.
 *
 * Deliberately NOT filtered by generated-PDF or by language. Both are already
 * filters the server applies for its own reasons, and naming a signature here
 * that was going to be dropped anyway costs nothing — whereas omitting one on
 * the browser's reading of "this document isn't generated" would hand the
 * server a claim it then has to second-guess.
 */
export function suppressedSignatures(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
): SuppressedSignature[] {
  const out: SuppressedSignature[] = [];
  const config = eSignStep(template)?.oneSpanConfig;
  if (!config) return out;

  for (const doc of config.documents) {
    for (const field of doc.fields) {
      if (isSignatureActive(field, values)) continue;
      out.push({ pdfEntryId: doc.pdfEntryId, textAnchor: field.textAnchor });
    }
  }
  return out;
}

/**
 * Role slugs left with nothing to sign, so the signer can come off the roster.
 *
 * A role qualifies only when it HAD signatures that would have counted for this
 * application — placed in the language being generated, in a document this
 * application actually produces — and every one of them is suppressed. A role
 * with no qualifying signature is deliberately left alone, because that covers
 * three cases where having no placement is normal and the signer is still real:
 *
 * - a legacy config with no geometry at all (the originator tagged the PDF by
 *   hand, so there is nothing here to reason about);
 * - a signature placed only in the other language;
 * - the 2nd/3rd instance of a repeatable role, whose placements all bind to the
 *   role's first-instance signer.
 *
 * The advisor is never returned. They countersign the package and have no
 * condition UI, matching `visibleSignerRoles` treating them as unconditional.
 */
export function rolesWithNothingToSign(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  language: 'en' | 'fr',
): Set<string> {
  const out = new Set<string>();
  const config = eSignStep(template)?.oneSpanConfig;
  if (!config) return out;

  const generated = new Set(collectGeneratedPdfEntryIds(template, values));
  const advisorSlugs = new Set(config.roles.filter((r) => r.type === 'advisor').map((r) => r.slug));

  /** slug → whether the role has any qualifying signature still active. */
  const active = new Map<string, boolean>();
  for (const doc of config.documents) {
    if (!generated.has(doc.pdfEntryId)) continue;
    for (const field of doc.fields) {
      if (!hasPlacementForLanguage(field, language)) continue;
      active.set(field.role, (active.get(field.role) ?? false) || isSignatureActive(field, values));
    }
  }

  for (const [slug, anyActive] of active) {
    if (!anyActive && !advisorSlugs.has(slug)) out.add(slug);
  }
  return out;
}

/**
 * The PDF entries this application actually produces a document for.
 *
 * A PDF entry can be gated by conditions over the advisor's answers, exactly as
 * a signer can. `BmoDataBuilder` would otherwise describe the signatures inside
 * an entry that was conditioned out — naming BMO a document the delivery
 * pipeline is never going to send.
 *
 * Uses the same `evaluateConditions` the form renderer uses for step / section /
 * field visibility, so "generated" here means precisely what the advisor sees
 * offered on the PDF Generation step.
 *
 * Lives here rather than in the front end because `rolesWithNothingToSign`
 * needs it too; `buildESignSubmission` and `AdditionalDocumentsStep` re-export
 * it from their own module.
 */
export function collectGeneratedPdfEntryIds(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
): string[] {
  const ids: string[] = [];
  for (const step of stepsWithPdfTemplates(template)) {
    for (const entry of step.fileEntries) {
      if (!evaluateConditions(entry.conditions, entry.conditionCombinator, values)) continue;
      ids.push(entry.id);
    }
  }
  return ids;
}

/**
 * Whether the signature has a box in the language being generated.
 *
 * A field with no placement in EITHER language is a legacy hand-tagged anchor —
 * language-agnostic, and never counted here (see `rolesWithNothingToSign`),
 * which is also how `BmoDataBuilder.HasPlacementForLanguage` treats it.
 */
function hasPlacementForLanguage(field: SignatureField, language: 'en' | 'fr'): boolean {
  if (!field.enPlacement && !field.frPlacement) return false;
  return (language === 'fr' ? field.frPlacement : field.enPlacement) !== null;
}

function eSignStep(template: WorkflowTemplate): WorkflowStep | null {
  return template.steps.find((s) => s.type === 'OneSpanESign' && s.oneSpanConfig) ?? null;
}
